// HeyWebCMS/js/router.js
import { DashboardView } from "./views/dashboard.js";
import { TemplatesView } from "./views/templates.js";
import { EditorView } from "./views/editor/editor.js";
import { SitePreviewView } from "./views/site-preview.js";
import { LoginView } from "./views/login.js";
import { RegisterView } from "./views/register.js";

/**
 * Router - SPA hash router com params por "/".
 *
 * ✅ Atualizações aplicadas:
 * - Parser mais robusto: suporta "#", "#/", "#editor", "#editor/.."
 * - Normaliza rota default com base no estado auth
 * - Atualiza estado ativo no menu lateral (classe .active)
 * - Hardening contra loops de redirect
 * - Error boundary mantido, com mais info útil no console
 */
export const Router = {
    routes: {
        "#": {
            title: "Escolher Modelo",
            render: TemplatesView.render,
            afterRender: TemplatesView.afterRender,
            public: true,
        },
        "#login": {
            title: "Entrar",
            render: LoginView.render,
            afterRender: LoginView.afterRender,
            public: true,
        },
        "#register": {
            title: "Criar Conta",
            render: RegisterView.render,
            afterRender: RegisterView.afterRender,
            public: true,
        },
        "#dashboard": {
            title: "Os Meus Projetos",
            render: DashboardView.render,
            afterRender: DashboardView.afterRender,
            public: false,
        },
        "#templates": {
            title: "Escolher Modelo",
            render: TemplatesView.render,
            afterRender: TemplatesView.afterRender,
            public: true,
        },
        "#editor": {
            title: "Editor de Site",
            render: EditorView.render,
            afterRender: EditorView.afterRender,
            public: false,
        },
        "#site": {
            title: "Visualizacao do Site",
            render: SitePreviewView.render,
            afterRender: SitePreviewView.afterRender,
            public: false,
        },
    },

    async init() {
        window.addEventListener("hashchange", () => this.handleRoute());
        await this.handleRoute();
    },

    // ------------------------
    // Helpers
    // ------------------------
    _getToken() {
        return localStorage.getItem("heyweb_token");
    },

    _normalizeHash(raw) {
        let h = String(raw || "").trim();
        if (!h) return "#";
        if (!h.startsWith("#")) h = `#${h}`;
        // remove trailing spaces only; keep URL-encoded stuff intact
        return h;
    },

    _parseHash(fullHash) {
        const h = this._normalizeHash(fullHash);

        // "#", "#/", "#dashboard", "#dashboard/", "#editor/new/x"
        const cleaned = h.replace(/\/+$/, ""); // remove trailing "/"
        const noHash = cleaned.startsWith("#") ? cleaned.slice(1) : cleaned;
        const noLeadingSlash = noHash.replace(/^\/+/, "");

        if (!noLeadingSlash) return { routeKey: "#", params: [] };

        const parts = noLeadingSlash.split("/").filter(Boolean);
        const routeKey = `#${parts[0]}`;
        const params = parts.slice(1);
        return { routeKey, params };
    },

    _defaultRouteForAuth() {
        const token = this._getToken();
        return token ? "#dashboard" : "#";
    },

    _setActiveNav(routeKey) {
        // Sidebar: <a href="#dashboard" class="nav-link">
        const links = document.querySelectorAll("#main-nav a.nav-link");
        links.forEach((a) => {
            const href = String(a.getAttribute("href") || "").trim();
            const isActive = href === routeKey;
            a.classList.toggle("active", isActive);
        });
    },

    _safeRedirect(targetHash) {
        const target = this._normalizeHash(targetHash);
        if (window.location.hash === target) return;
        window.location.hash = target;
    },

    // ------------------------
    // Core
    // ------------------------
    async handleRoute() {
        const appContainer = document.getElementById("app");
        if (!appContainer) return;

        const fullHash = window.location.hash || "#";
        const { routeKey, params } = this._parseHash(fullHash);

        const token = this._getToken();

        // resolve unknown route
        let route = this.routes[routeKey];

        // unknown -> default
        if (!route) {
            const fallback = this._defaultRouteForAuth();
            this._safeRedirect(fallback);
            return;
        }

        // auth guard
        if (!route.public && !token) {
            // evita loop: se já estamos em login, não redireciona novamente
            if (routeKey !== "#login") {
                console.warn("[ROUTER] Acesso negado: redirecionando para login.");
                this._safeRedirect("#login");
            }
            return;
        }

        // se user autenticado e tentar ir login/register manualmente, manda para dashboard
        if (token && (routeKey === "#login" || routeKey === "#register")) {
            this._safeRedirect("#dashboard");
            return;
        }

        // UI state
        this._setActiveNav(routeKey);
        document.title = route.title ? `SiteEiro | ${route.title}` : "SiteEiro";

        try {
            // Render
            const html = await route.render(params);
            appContainer.innerHTML = html;

            // AfterRender
            if (typeof route.afterRender === "function") {
                await route.afterRender(params);
            }

            window.scrollTo(0, 0);
        } catch (error) {
            console.error("ERRO CRÍTICO NO ROUTER:", { routeKey, params, error });

            appContainer.innerHTML = `
        <div style="padding:4rem; text-align:center; font-family:Inter,system-ui,Segoe UI,Roboto,Arial,sans-serif;">
          <h2 style="color:#ef4444; margin:0;">⚠️ Erro de Renderização</h2>
          <p style="color:#64748b; margin:0.75rem 0 0;">
            Não foi possível carregar esta vista. Tenta atualizar a página.
          </p>
          <div style="margin-top:1.25rem; display:flex; justify-content:center; gap:0.75rem; flex-wrap:wrap;">
            <button id="hw-reload"
              style="padding:0.6rem 1.2rem; background:#0f172a; color:white; border:none; border-radius:0.6rem; cursor:pointer; font-weight:800;">
              Recarregar
            </button>
            <button id="hw-go-dashboard"
              style="padding:0.6rem 1.2rem; background:white; color:#0f172a; border:1px solid #e2e8f0; border-radius:0.6rem; cursor:pointer; font-weight:800;">
              Ir para Dashboard
            </button>
          </div>
        </div>
      `;

            const btnReload = document.getElementById("hw-reload");
            if (btnReload) btnReload.onclick = () => window.location.reload();

            const btnDash = document.getElementById("hw-go-dashboard");
            if (btnDash) btnDash.onclick = () => this._safeRedirect("#dashboard");
        }
    },
};
